import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Printer,
  Download,
  Share2,
  Loader2,
  RefreshCw,
  X
} from "lucide-react";
import {
  buildFacilityColorMap,
  findConflictingMeetingIds,
  PERSONAL_COLOR,
  DEFAULT_WORK_COLOR,
  CONFLICT_COLOR,
} from "@/lib/calendarUtils";
import { SESSION_TOKEN_KEY } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Motivational quotes for productivity
const PRODUCTIVITY_QUOTES = [
  { quote: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { quote: "Focus on being productive instead of busy.", author: "Tim Ferriss" },
  { quote: "Small daily improvements over time lead to stunning results.", author: "Robin Sharma" },
  { quote: "Excellence is not a destination but a continuous journey.", author: "Brian Tracy" },
  { quote: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { quote: "Your limitation—it's only your imagination.", author: "Unknown" },
  { quote: "Dream it. Wish it. Do it.", author: "Unknown" },
  { quote: "Success is the sum of small efforts repeated day in and day out.", author: "Robert Collier" },
  { quote: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { quote: "The future depends on what you do today.", author: "Mahatma Gandhi" },
];

// Format time helper
const formatHour = (hour) => {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const displayH = h > 12 ? h - 12 : h === 0 ? 12 : h;
  return `${displayH}:${m.toString().padStart(2, '0')} ${ampm}`;
};

export default function PrintableDayPlanner({
  date: propDate,
  meetings: propMeetings = [],
  tasks: propTasks = [],
  facilities = [],
  facilityColorMap: facilityColorMapProp,
  userName = "Leader",
  onClose
}) {
  const printRef = useRef(null);
  const [quote] = useState(() => PRODUCTIVITY_QUOTES[Math.floor(Math.random() * PRODUCTIVITY_QUOTES.length)]);
  const [wisdom, setWisdom] = useState(null);
  const [monthMeetings, setMonthMeetings] = useState(null); // null until fetched

  // --------------------------------------------------------------------------
  // User-selectable date. The modal opens on whatever date the parent passed
  // (usually "today" or the currently-viewed calendar date) but the user can
  // pick any date from the header date-picker and the preview + PDF rebuild.
  // --------------------------------------------------------------------------
  const initialDate = propDate || new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(initialDate);
  // Ignore any props passed in — the parent's data may be stale. We always
  // fetch fresh on mount so re-opening the modal picks up brand-new tasks
  // and meetings the user just entered.
  const [dayMeetings, setDayMeetings] = useState([]);
  const [dayTasks, setDayTasks] = useState([]);
  const [loadingDate, setLoadingDate] = useState(true); // fetching on mount
  const [refreshKey, setRefreshKey] = useState(0); // bump to force a re-fetch

  // Expose the chosen date + filtered data under the original names so the
  // rest of this component keeps working without a 600-line rename pass.
  const date = selectedDate;
  const meetings = dayMeetings;
  const tasks = dayTasks;

  // Always re-fetch meetings + tasks for the picked date. We don't trust the
  // props because the parent's data may be stale (e.g. user edited a task on
  // /tasks then opened the planner from /calendar without a Calendar refresh).
  // Re-runs when selectedDate OR refreshKey changes. Cache-busting `_t` param
  // guarantees the browser never serves a stale response.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDate(true);
      try {
        const bust = Date.now();
        const [meetingsRes, tasksRes] = await Promise.all([
          axios.get(`${API}/meetings`, {
            params: { date: selectedDate, _t: bust },
            headers: { "Cache-Control": "no-cache" },
            withCredentials: true,
          }),
          axios.get(`${API}/tasks`, {
            params: { _t: bust },
            headers: { "Cache-Control": "no-cache" },
            withCredentials: true,
          }),
        ]);
        if (cancelled) return;
        const allMtgs = Array.isArray(meetingsRes.data) ? meetingsRes.data : [];
        // Expand recurring client-side: only meetings that match the picked date.
        const matches = (m) => {
          const md = m.date;
          if (md === selectedDate) return true;
          const rec = m.recurring;
          const end = m.recurring_end_date;
          if (!rec || !md || md > selectedDate) return false;
          if (end && selectedDate > end) return false;
          const base = new Date(md + "T00:00:00");
          const target = new Date(selectedDate + "T00:00:00");
          if (rec === "daily") return true;
          if (rec === "weekly") return base.getDay() === target.getDay();
          if (rec === "biweekly") {
            const days = Math.round((target - base) / 86400000);
            return days >= 0 && days % 14 === 0;
          }
          if (rec === "monthly") return base.getDate() === target.getDate();
          return false;
        };
        setDayMeetings(allMtgs.filter(matches));
        const nextTasks = Array.isArray(tasksRes.data) ? tasksRes.data : [];
        setDayTasks(nextTasks);
        // On manual refresh (refreshKey > 0), give the user clear feedback
        // that we pulled fresh data — including how many open tasks landed
        // in the day view.
        if (refreshKey > 0) {
          const openCount = nextTasks.filter(t => !t.completed).length;
          toast.success(`Refreshed · ${openCount} open task${openCount === 1 ? "" : "s"}`);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to load date data", err);
        }
      } finally {
        if (!cancelled) setLoadingDate(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedDate, refreshKey]);

  // Fetch daily leadership wisdom for the selected date (context-matched)
  useEffect(() => {
    let cancelled = false;
    const fetchWisdom = async () => {
      try {
        const params = date ? { date } : { contextual: true };
        const res = await axios.get(`${API}/wisdom/daily`, {
          params,
          withCredentials: true,
        });
        if (!cancelled) setWisdom(res.data);
      } catch (err) {
        if (!cancelled) setWisdom(null);
      }
    };
    fetchWisdom();
    return () => { cancelled = true; };
  }, [date]);

  // Fetch the FULL month's meetings (server expands recurring ones), so the
  // page-2 month grid is complete regardless of which view the user printed
  // from (day/week/month).
  useEffect(() => {
    if (!date) return;
    let cancelled = false;
    const fetchMonth = async () => {
      try {
        const target = new Date(date + "T00:00:00");
        const y = target.getFullYear();
        const m = target.getMonth();
        const monthStart = `${y}-${String(m + 1).padStart(2, "0")}-01`;
        // last day of the month
        const lastDate = new Date(y, m + 1, 0).getDate();
        const monthEnd = `${y}-${String(m + 1).padStart(2, "0")}-${String(lastDate).padStart(2, "0")}`;
        const res = await axios.get(
          `${API}/meetings/calendar?start_date=${monthStart}&end_date=${monthEnd}`,
          { withCredentials: true }
        );
        if (!cancelled) setMonthMeetings(res.data || []);
      } catch (err) {
        if (!cancelled) setMonthMeetings([]); // fall back to whatever was passed in
      }
    };
    fetchMonth();
    return () => { cancelled = true; };
  }, [date, refreshKey]);

  // Effective meeting list for the month grid: prefer the freshly-fetched
  // full-month list (with recurring expanded). Fall back to the parent's
  // `meetings` prop while the fetch is in flight or if it failed.
  const effectiveMonthMeetings =
    monthMeetings && monthMeetings.length >= 0 ? monthMeetings : meetings;

  // Stable facility color map (prop from parent if provided, else rebuild)
  const facilityColorMap = facilityColorMapProp || buildFacilityColorMap(facilities);

  // Create facility info map (still needed for name/short_name lookup)
  const facilityMap = {};
  facilities.forEach(f => {
    facilityMap[f.facility_id] = f;
  });

  // Get facility info for a meeting — color from map, name from facility record
  const getFacilityInfo = (meeting) => {
    if (meeting.category === "personal") {
      return { name: "Personal", short_name: "Personal", color: PERSONAL_COLOR };
    }
    if (meeting.facility_id && facilityMap[meeting.facility_id]) {
      const f = facilityMap[meeting.facility_id];
      return {
        ...f,
        color: facilityColorMap[meeting.facility_id] || f.color || DEFAULT_WORK_COLOR,
      };
    }
    return { name: "Work", short_name: "Work", color: DEFAULT_WORK_COLOR };
  };
  
  // Filter meetings for the current day only (for page 1)
  const visibleDayMeetings = meetings.filter(m => m.date === date);

  // Sort day meetings by time
  const sortedMeetings = [...visibleDayMeetings].sort((a, b) => a.start_time - b.start_time);

  // Detect conflicts among today's meetings
  const dayConflictIds = findConflictingMeetingIds(visibleDayMeetings);
  
  // Filter tasks for the day — completed tasks fall off so the planner only
  // shows what's actually still open. A task counts as "for today" if EITHER:
  //   • it has priority "Today" (carried forward day-to-day), OR
  //   • its due date is today, OR
  //   • its due date is in the past (overdue — needs to be on this planner)
  // We sort: overdue first (oldest first), then due-today, then today-priority.
  const visibleDayTasks = tasks
    .filter(t => {
      if (t.completed) return false;
      const taskDue = t.due_date || t.deadline;
      if (taskDue && taskDue <= date) return true;
      if (t.priority === "Today") return true;
      return false;
    })
    .sort((a, b) => {
      const ad = a.due_date || a.deadline || "";
      const bd = b.due_date || b.deadline || "";
      // Overdue/due bubbles up; priority-only ("Today" with no due) goes last
      if (!ad && !bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return ad.localeCompare(bd);
    });

  // Get upcoming deadlines (within 7 days) — exclude completed
  const upcomingDeadlines = tasks.filter(t => {
    if (t.completed) return false;
    const deadline = t.due_date || t.deadline;
    if (!deadline) return false;
    const deadlineDate = new Date(deadline);
    const targetDate = new Date(date);
    const diffDays = Math.ceil((deadlineDate - targetDate) / (1000 * 60 * 60 * 24));
    return diffDays > 0 && diffDays <= 7;
  }).slice(0, 5);

  // Get MAJOR deadlines (within 30 days) — exclude completed
  const majorDeadlines = tasks.filter(t => {
    if (t.completed) return false;
    const deadline = t.due_date || t.deadline;
    if (!deadline) return false;
    // Include high priority tasks or tasks with "deadline" in category
    const isImportant = t.priority === "high" || t.importance === "high" || 
                        (t.category && t.category.toLowerCase().includes('deadline'));
    const deadlineDate = new Date(deadline);
    const targetDate = new Date(date);
    const diffDays = Math.ceil((deadlineDate - targetDate) / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= 30;
  }).sort((a, b) => {
    const dateA = new Date(a.due_date || a.deadline);
    const dateB = new Date(b.due_date || b.deadline);
    return dateA - dateB;
  }).slice(0, 8);
  
  // Generate monthly calendar data
  const getMonthCalendar = () => {
    const targetDate = date ? new Date(date + 'T00:00:00') : new Date();
    const year = targetDate.getFullYear();
    const month = targetDate.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    const totalDays = lastDay.getDate();
    
    const days = [];
    // Padding for start of month
    for (let i = 0; i < startPadding; i++) {
      days.push(null);
    }
    // Actual days
    for (let d = 1; d <= totalDays; d++) {
      const dayStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayMeetings = effectiveMonthMeetings.filter(m => m.date === dayStr);
      const dayDeadlines = tasks.filter(t => !t.completed && (t.due_date || t.deadline) === dayStr);
      const dayConflicts = findConflictingMeetingIds(dayMeetings);
      days.push({
        day: d,
        date: dayStr,
        isToday: dayStr === date,
        hasMeetings: dayMeetings.length > 0,
        hasDeadlines: dayDeadlines.length > 0,
        hasConflict: dayConflicts.size > 0,
        conflictIds: dayConflicts,
        meetings: dayMeetings,
        deadlines: dayDeadlines
      });
    }
    
    return {
      monthName: firstDay.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
      days
    };
  };
  
  const monthCalendar = getMonthCalendar();
  
  // Calculate time blocks — HOURLY, 5 AM through 6 PM (14 rows). Meetings
  // are anchored to the hour they start in; duration is on the start-end
  // label so we don't duplicate the same meeting across rows.
  const timeBlocks = [];
  for (let slot = 5; slot <= 18; slot += 1) {
    const slotMeetings = sortedMeetings.filter(m => Math.floor(m.start_time) === slot);
    timeBlocks.push({ hour: slot, meetings: slotMeetings });
  }
  
  // Pre-rendered PDF state — populated on modal mount so the action buttons
  // can open it INSTANTLY (no async-on-click → no Safari user-gesture races).
  const [pdfReady, setPdfReady] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [pdfInlineUrl, setPdfInlineUrl] = useState(null);
  const [pdfDownloadUrl, setPdfDownloadUrl] = useState(null);

  const dateLabelForFile = (date || new Date().toISOString().slice(0, 10));
  const pdfFilename = `niko-day-planner-${dateLabelForFile}.pdf`;

  /**
   * Render the on-screen printable preview to a PDF Blob.
   *
   * IMPORTANT: We do NOT use html2pdf's auto-pagination because it
   * tends to add a phantom 3rd blank page when the canvas height is
   * close to (but not exactly) two Letter pages. Instead we:
   *   1. Capture each `.pdf-page` div individually with html2canvas
   *   2. Add each canvas as its own full Letter page via jsPDF directly
   *
   * Result: PDF always has exactly N pages where N = number of
   * .pdf-page elements (currently 2 — day planner + monthly snapshot),
   * and each page maps 1:1 onto Letter without rescaling so fonts
   * print at their intended size.
   */
  const renderPreviewBlob = async () => {
    const printContent = printRef.current;
    if (!printContent) throw new Error("Preview not ready");
    const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const pageEls = printContent.querySelectorAll(".pdf-page");
    if (pageEls.length === 0) throw new Error("No .pdf-page elements found");

    const pdf = new jsPDF({
      unit: "in",
      format: "letter", // 8.5 × 11
      orientation: "portrait",
      compress: true,
    });
    const PAGE_W = 8.5;
    const PAGE_H = 11;

    for (let i = 0; i < pageEls.length; i++) {
      const el = pageEls[i];
      // 4× scale → ~288 DPI raster — true print quality
      const canvas = await html2canvas(el, {
        scale: 4,
        backgroundColor: "#ffffff",
        useCORS: true,
        logging: false,
        letterRendering: false,
        windowWidth: 768,
        width: 768,
        height: el.offsetHeight,
      });
      const img = canvas.toDataURL("image/jpeg", 0.97);
      if (i > 0) pdf.addPage("letter", "portrait");
      // Center the captured 8 × 10.26 in image inside 8.5 × 11 in Letter
      // → 0.25 in horizontal margin, 0.37 in vertical margin
      const imgAspect = canvas.height / canvas.width; // captured page aspect
      const drawW = PAGE_W - 0.5;        // 8 in
      const drawH = drawW * imgAspect;   // proportionate
      const x = (PAGE_W - drawW) / 2;
      const y = (PAGE_H - drawH) / 2;
      pdf.addImage(img, "JPEG", x, y, drawW, drawH, undefined, "FAST");
    }
    return pdf.output("blob");
  };

  /**
   * Render the preview to a PDF and upload it to the backend cache. Returns
   * { inlineUrl, downloadUrl } — both use a token-protected cache_id GET.
   * Inline = Safari/iPad displays in-tab; Download = forces save dialog.
   */
  const renderAndCachePdf = async () => {
    const blob = await renderPreviewBlob();
    const token = localStorage.getItem(SESSION_TOKEN_KEY) || "";
    const res = await axios.post(`${API}/calendar/render-pdf`, blob, {
      headers: { "Content-Type": "application/pdf" },
      params: token ? { token } : undefined,
      withCredentials: true,
    });
    const cacheId = res.data.cache_id;
    const tokenQ = token ? `&token=${encodeURIComponent(token)}` : "";
    return {
      blob,
      cacheId,
      inlineUrl: `${API}/calendar/pdf/${cacheId}?_t=${Date.now()}${tokenQ}`,
      downloadUrl: `${API}/calendar/pdf/${cacheId}?download=1&_t=${Date.now()}${tokenQ}`,
    };
  };

  /**
   * Pre-render the PDF as soon as the modal mounts (and again whenever the
   * underlying data changes). This makes the action buttons INSTANT — no
   * async-on-click — which is the only reliable pattern on iPad/iOS Safari.
   */
  useEffect(() => {
    let cancelled = false;
    setPdfReady(false);
    setPdfError(null);
    setPdfBlob(null);
    setPdfInlineUrl(null);
    setPdfDownloadUrl(null);

    // Wait one frame so printRef is mounted, plus a small delay so any
    // late-arriving wisdom/notes content is in the DOM before snapshot.
    const timer = setTimeout(async () => {
      if (cancelled) return;
      try {
        const { blob, inlineUrl, downloadUrl } = await renderAndCachePdf();
        if (cancelled) return;
        setPdfBlob(blob);
        setPdfInlineUrl(inlineUrl);
        setPdfDownloadUrl(downloadUrl);
        setPdfReady(true);
      } catch (err) {
        if (cancelled) return;
        console.error("PDF pre-render failed", err);
        setPdfError(err?.message || "Could not build the PDF");
      }
    }, 400);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, meetings, tasks, wisdom, monthMeetings]);

  /**
   * Tap to Download — opens the pre-rendered PDF inline in a new tab.
   * iPad/iOS Safari will display the PDF in the tab; the user uses the
   * built-in toolbar (Save to Files, Mail, Print, AirDrop) to act on it.
   *
   * Synchronous + no async work in the handler = no Safari user-gesture
   * race condition, no popup blocker tripping, no blank tab.
   */
  const handleDownloadPdf = () => {
    if (!pdfReady || !pdfInlineUrl) {
      alert(
        pdfError
          ? `Couldn't build the PDF: ${pdfError}. Please close and re-open this preview.`
          : "Still preparing your PDF — give it a couple seconds and tap again."
      );
      return;
    }
    window.open(pdfInlineUrl, "_blank", "noopener,noreferrer");
  };

  /**
   * Print — opens the same pre-rendered PDF in a new tab. The PDF viewer
   * (Safari/Chrome/Firefox) has a built-in Print button that respects the
   * embedded Letter size and 2-page break.
   */
  const handlePrint = () => {
    if (!pdfReady || !pdfInlineUrl) {
      alert(
        pdfError
          ? `Couldn't build the PDF: ${pdfError}. Please close and re-open this preview.`
          : "Still preparing your PDF — give it a couple seconds and tap again."
      );
      return;
    }
    const win = window.open(pdfInlineUrl, "_blank", "noopener,noreferrer");
    if (!win) {
      alert("Popup blocked. Use Tap to Download, then print from the PDF viewer.");
      return;
    }
    // Best-effort auto-print after the viewer loads (works in some browsers,
    // user can always ⌘P / Ctrl+P inside the new tab).
    try {
      win.addEventListener("load", () => {
        try { win.focus(); win.print(); } catch (_) { /* user can ⌘P */ }
      }, { once: true });
    } catch (_) { /* cross-origin restrictions */ }
  };

  /**
   * Share — uses the pre-rendered blob with the native share sheet on
   * iOS / iPadOS (Mail, Messages, AirDrop, Save to Files). Falls back to
   * opening the inline cache URL on desktop.
   */
  const handleSharePdf = async () => {
    if (!pdfReady || !pdfBlob) {
      alert(
        pdfError
          ? `Couldn't build the PDF: ${pdfError}. Please close and re-open this preview.`
          : "Still preparing your PDF — give it a couple seconds and tap again."
      );
      return;
    }
    try {
      const file = new File([pdfBlob], pdfFilename, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Niko Day Planner",
          text: `Day planner for ${dateLabelForFile}`,
        });
      } else if (pdfInlineUrl) {
        // Desktop fallback — open the inline tab so user can save/share.
        window.open(pdfInlineUrl, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        console.error("Share failed", err);
        alert("Sharing didn't complete. Try Tap to Download instead.");
      }
    }
  };

  const dateObj = date ? new Date(date + 'T00:00:00') : new Date();
  const formattedDate = dateObj.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });

  return (
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto"
      onClick={(e) => {
        // Close when clicking the backdrop (not the modal content)
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-xl max-w-4xl w-full flex flex-col"
        style={{
          maxHeight: '95vh',
          // Safari needs an explicit height context for the inner scroller
          // to work reliably. We rely on flex children with min-h-0 below.
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Toolbar — sticky so it stays visible even when content scrolls.
            z-10 lifts it above the printable preview below. */}
        <div className="sticky top-0 z-10 flex-shrink-0 bg-zinc-900 text-white p-3 sm:p-4 rounded-t-xl no-print">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Printer className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="font-semibold">Daily Planner Preview</span>
            {/* Date picker — change which date the planner prints.
                When changed, preview + PDF rebuild automatically for the
                new day. Min date is 2 years back so the user can print
                historical days (e.g. for retrospectives) too. */}
            <div className="ml-auto flex items-center gap-2">
              <label
                htmlFor="planner-date"
                className="text-xs text-zinc-300 whitespace-nowrap"
              >
                Date:
              </label>
              <input
                id="planner-date"
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v) setSelectedDate(v);
                }}
                className="bg-zinc-800 border border-zinc-700 text-white text-sm rounded px-2 py-1
                           focus:outline-none focus:ring-1 focus:ring-violet-500"
                data-testid="planner-date-picker"
                aria-label="Choose planner date"
              />
              {loadingDate && (
                <Loader2 className="w-4 h-4 animate-spin text-violet-300" />
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-stretch gap-2">
            {/* Tap to Download — INSTANT open of the pre-rendered PDF.
                Pre-rendering happens on modal mount, so by the time the
                user sees the toolbar the PDF is usually already cached.
                We use <Button> (not <a>) because the inline URL changes
                each render and we want a single source of truth. */}
            <Button
              onClick={handleDownloadPdf}
              disabled={!pdfReady && !pdfError}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
              data-testid="download-pdf-btn"
              aria-label="Tap to download day planner PDF"
            >
              {!pdfReady && !pdfError ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Preparing…
                </>
              ) : pdfError ? (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Retry
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  Tap to Download
                </>
              )}
            </Button>
            <Button
              onClick={handlePrint}
              disabled={!pdfReady}
              className="bg-primary hover:bg-primary/90"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
            <Button
              onClick={handleSharePdf}
              disabled={!pdfReady}
              variant="outline"
              className="bg-blue-600/20 border-blue-500/40 text-white hover:bg-blue-600/40"
              data-testid="share-pdf-btn"
              title="Share via email, AirDrop, Messages, or save to Files"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share / Email
            </Button>
            <Button
              onClick={() => setRefreshKey(k => k + 1)}
              variant="outline"
              disabled={loadingDate}
              className="bg-violet-500/20 border-violet-500/40 text-white hover:bg-violet-500/40"
              data-testid="refresh-planner-btn"
              title="Re-fetch today's tasks and meetings"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingDate ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              onClick={onClose}
              className="bg-red-500/20 border-red-500 text-white hover:bg-red-500/40"
              data-testid="close-print-modal"
            >
              <X className="w-4 h-4 mr-1" />
              Close
            </Button>
          </div>
        </div>

        {/* Printable Content — dedicated scroll region (Safari-safe) */}
        <div
          className="flex-1 min-h-0 overflow-y-auto"
          style={{ WebkitOverflowScrolling: 'touch' }}
          data-testid="print-modal-scroll"
        >
          {/* Printable preview content (also serves as the on-screen view).
              Width is FIXED to exactly 8in (768 px at 96 DPI) so the
              html2canvas snapshot maps 1:1 into Letter without rescaling.
              Outer modal scroll-region provides the screen-side padding;
              the printRef itself ships only the page-content. */}
          <div className="bg-zinc-100 py-8 flex justify-center">
            <div
              ref={printRef}
              className="bg-white shadow-md"
              style={{
                color: '#0f172a',
                fontFamily: 'Helvetica, Arial, sans-serif',
                WebkitFontSmoothing: 'antialiased',
                width: '768px',     // exactly 8 in (Letter inner content area)
                boxSizing: 'border-box',
              }}
            >
            <div className="pdf-page" style={{ width: '768px', height: '985px', overflow: 'hidden', boxSizing: 'border-box', padding: '40px 44px' }}>
            {/* =================== HEADER (Page 1) =================== */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', paddingBottom: '14px', borderBottom: '3px solid #7c3aed', marginBottom: '18px' }}>
              <div>
                <h1 style={{ fontSize: '26px', fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.01em', lineHeight: 1 }}>
                  Daily Planner
                </h1>
                <div style={{ fontSize: '15px', color: '#334155', fontWeight: 600, marginTop: '6px' }}>{formattedDate}</div>
                <div style={{ fontSize: '12px', color: '#7c3aed', fontWeight: 700, marginTop: '6px', letterSpacing: '0.03em', textTransform: 'uppercase' }}>
                  {userName?.split(' ')[0] || 'Leader'} — let&apos;s make today count
                </div>
              </div>
              <div style={{ maxWidth: '280px', padding: '10px 14px', background: '#f5f3ff', borderRadius: '6px', borderLeft: '4px solid #7c3aed' }}>
                <div style={{ fontStyle: 'italic', fontSize: '11px', color: '#1e293b', lineHeight: 1.5 }}>&ldquo;{quote.quote}&rdquo;</div>
                <div style={{ fontSize: '10px', color: '#7c3aed', marginTop: '6px', fontWeight: 700, textAlign: 'right', letterSpacing: '0.02em' }}>— {quote.author}</div>
              </div>
            </div>

            {/* =================== FOCUS BANNER — One Bet + Top 3 =================== */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '14px', marginBottom: '14px' }}>
              {/* Left: One Bet + Top 3 non-negotiables */}
              <div style={{ border: '2px solid #7c3aed', borderRadius: '6px', padding: '10px 12px', background: '#faf5ff' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '3px' }}>
                  IF I only do ONE thing today
                </div>
                <div style={{ height: '20px', borderBottom: '1.5px solid #7c3aed', marginBottom: '10px' }} />
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '4px' }}>
                  Top 3 non-negotiable outcomes
                </div>
                {[1, 2, 3].map(n => (
                  <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                    <span style={{ fontSize: '10px', fontWeight: 800, color: '#7c3aed', width: '14px' }}>{n}.</span>
                    <div style={{ flex: 1, height: '16px', borderBottom: '1px solid #cbd5e1' }} />
                  </div>
                ))}
              </div>
              {/* Right: Wellness check strip */}
              <div style={{ border: '1.5px solid #10B981', borderRadius: '6px', padding: '10px 12px', background: '#f0fdf4' }}>
                <div style={{ fontSize: '10px', fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px' }}>
                  Wellness check
                </div>
                {[
                  ['Sleep 7+ hrs', 'AM check-in'],
                  ['Water · 64 oz', 'AM · Mid · PM'],
                  ['Walk / Move', '10 min minimum'],
                  ['Wellness break', 'Step away · breathe'],
                ].map(([label, hint]) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px' }}>
                    <div style={{ width: '13px', height: '13px', border: '1.5px solid #059669', borderRadius: '3px', flexShrink: 0 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#0f172a' }}>{label}</span>
                      <span style={{ fontSize: '8px', color: '#059669', fontWeight: 500 }}>{hint}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* =================== PRE-MEETING REFLECTION =================== */}
            {(() => {
              // Curated reflection pool — mirrors backend list ids, seeded by
              // date so the same printout always shows the same quote.
              const pool = [
                { c: 'Presence',  t: "Take a breath. We're not here to react — we're here to think together." },
                { c: 'Presence',  t: "Our patients don't care what's on our calendar. They care what's in our attention. Let's spend ours well." },
                { c: 'Listening', t: "The quietest person in the room often has the answer no one else thought of. Whose voice haven't we heard yet?" },
                { c: 'Listening', t: "Ask twice before you tell once. Most miscommunication is unasked questions." },
                { c: 'Decision',  t: "What would we choose if we knew no one would push back?" },
                { c: 'Decision',  t: "Good decisions need a name attached. Who owns this when the meeting ends?" },
                { c: 'Courage',   t: "What's the thing we keep talking around instead of about? Let's name it." },
                { c: 'Courage',   t: "I'd rather be uncomfortable in the room than unsafe on the unit." },
              ];
              // Deterministic pick by ISO date so print & re-print match
              const seed = date.split('-').reduce((a, s) => a + parseInt(s, 10), 0);
              const r = pool[seed % pool.length];
              return (
                <div style={{ border: '1px solid #cbd5e1', borderLeft: '4px solid #F59E0B', borderRadius: '4px', padding: '8px 12px', marginBottom: '14px', background: '#fffbeb' }}>
                  <div style={{ fontSize: '9px', fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '2px' }}>
                    Pre-Meeting Reflection · {r.c}
                  </div>
                  <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#0f172a', lineHeight: 1.4 }}>&ldquo;{r.t}&rdquo;</div>
                </div>
              );
            })()}

            {/* =================== TWO-COLUMN BODY =================== */}
            <div style={{ display: 'flex', gap: '22px' }}>
              {/* -------- LEFT: Schedule -------- */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0f172a', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #7c3aed' }}>
                  Today&apos;s Schedule
                </div>

                {timeBlocks.map(({ hour, meetings: hourMeetings }) => {
                  return (
                  <div key={hour} style={{ display: 'flex', minHeight: '34px', borderBottom: '1px solid #cbd5e1', background: hour % 2 === 0 ? '#f8fafc' : 'white' }}>
                    <div style={{ width: '68px', fontSize: '11px', fontWeight: 700, color: '#475569', padding: '6px 8px 6px 0', textAlign: 'right', borderRight: '2px solid #cbd5e1' }}>
                      {formatHour(hour)}
                    </div>
                    <div style={{ flex: 1, padding: '3px 8px', minWidth: 0 }}>
                      {hourMeetings.length > 0 ? hourMeetings.map((meeting, idx) => {
                        const facilityInfo = getFacilityInfo(meeting);
                        const isConflict = dayConflictIds.has(meeting.meeting_id);
                        return (
                          <div
                            key={idx}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              backgroundColor: facilityInfo.color + '22',
                              borderLeft: `4px solid ${facilityInfo.color}`,
                              outline: isConflict ? `1.5px solid ${CONFLICT_COLOR}` : undefined,
                              marginBottom: '2px',
                              fontSize: '11px',
                              color: '#0f172a',
                              fontWeight: 500
                            }}
                          >
                            {isConflict && (
                              <span style={{
                                background: CONFLICT_COLOR,
                                color: 'white',
                                fontSize: '8px',
                                fontWeight: 800,
                                padding: '1px 5px',
                                borderRadius: '3px',
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                flexShrink: 0
                              }}>
                                CONFLICT
                              </span>
                            )}
                            <span style={{ fontWeight: 700 }}>{meeting.title}</span>
                            <span style={{ fontSize: '10px', color: '#475569', fontWeight: 600 }}>
                              {formatHour(meeting.start_time)}–{formatHour(meeting.end_time)}
                            </span>
                            {meeting.location && (
                              <span style={{ fontSize: '10px', color: '#64748b' }}>· {meeting.location}</span>
                            )}
                            {/* Post-meeting rating — circle 1-5 after meeting */}
                            <span style={{ marginLeft: 'auto', display: 'flex', gap: '2px', flexShrink: 0 }}>
                              {[1, 2, 3, 4, 5].map(n => (
                                <span key={n} style={{ width: '9px', height: '9px', border: '1px solid #94a3b8', borderRadius: '50%', display: 'inline-block' }} />
                              ))}
                            </span>
                          </div>
                        );
                      }) : null}
                    </div>
                  </div>
                  );
                })}

                {/* Legend — subtle and tight */}
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginTop: '14px', paddingTop: '10px', borderTop: '1px solid #cbd5e1' }}>
                  {facilities.map(f => (
                    <div key={f.facility_id} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: '#334155' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: facilityColorMap[f.facility_id] || f.color || '#8B5CF6' }} />
                      <span style={{ fontWeight: 500 }}>{f.short_name || f.name}</span>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: '#334155' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: PERSONAL_COLOR }} />
                    <span style={{ fontWeight: 500 }}>Personal</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '10px', color: '#334155' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: CONFLICT_COLOR }} />
                    <span style={{ fontWeight: 500 }}>Conflict</span>
                  </div>
                </div>
              </div>

              {/* -------- RIGHT: Tasks / Priorities / Wisdom -------- */}
              <div style={{ width: '280px', flexShrink: 0 }}>
                {/* Tasks */}
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0f172a', marginBottom: '8px', paddingBottom: '4px', borderBottom: '2px solid #7c3aed' }}>
                    Today&apos;s Tasks
                  </div>
                  {/* Show ALL open/overdue tasks — no cap. Compact styling keeps them all on one page. */}
                  {visibleDayTasks.map((task, idx) => {
                    const taskDue = task.due_date || task.deadline;
                    const isOverdue = taskDue && taskDue < date;
                    return (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', padding: '3px 0', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ width: '11px', height: '11px', border: `1.5px solid ${isOverdue ? '#dc2626' : '#7c3aed'}`, borderRadius: '3px', flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '9.5px', lineHeight: 1.35, color: '#0f172a', fontWeight: 500 }}>
                          {isOverdue && (
                            <span style={{ display: 'inline-block', background: '#dc2626', color: 'white', fontSize: '7px', fontWeight: 800, padding: '0px 4px', borderRadius: '3px', letterSpacing: '0.04em', textTransform: 'uppercase', marginRight: '4px', verticalAlign: '1px' }}>
                              OVERDUE
                            </span>
                          )}
                          {task.description}
                        </div>
                        {(task.category || taskDue) && (
                          <div style={{ fontSize: '8px', color: '#7c3aed', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '0px' }}>
                            {task.category}
                            {task.category && taskDue && ' · '}
                            {taskDue && `due ${taskDue}`}
                          </div>
                        )}
                      </div>
                    </div>
                    );
                  })}
                  {/* Blank lines for handwritten tasks — only if we have room after listing everything. */}
                  {visibleDayTasks.length < 7 && Array.from({ length: 7 - visibleDayTasks.length }).map((_, idx) => (
                    <div key={`blank-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '20px', borderBottom: '1px solid #e2e8f0' }}>
                      <div style={{ width: '11px', height: '11px', border: '1.5px solid #cbd5e1', borderRadius: '3px', flexShrink: 0 }} />
                    </div>
                  ))}
                </div>

                {/* Top 3 Priorities */}
                <div style={{ marginBottom: '16px', padding: '12px 14px', background: '#faf5ff', borderRadius: '6px', border: '1px solid #e9d5ff' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#6b21a8', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Top 3 Priorities Today</div>
                  {[1, 2, 3].map(num => (
                    <div key={num} style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '24px', marginBottom: '6px' }}>
                      <div style={{ width: '20px', height: '20px', background: '#7c3aed', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, flexShrink: 0 }}>{num}</div>
                      <div style={{ flex: 1, borderBottom: '1.5px solid #cbd5e1', height: '1px' }}></div>
                    </div>
                  ))}
                </div>

                {/* Major Deadlines - Always Show */}
                {majorDeadlines.length > 0 && (
                  <div style={{ marginBottom: '16px', padding: '12px 14px', background: '#fef2f2', borderRadius: '6px', border: '1px solid #fecaca' }}>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#b91c1c', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Major Deadlines (30 Days)</div>
                    {majorDeadlines.map((task, idx) => {
                      const deadline = task.due_date || task.deadline;
                      const daysLeft = Math.ceil((new Date(deadline) - new Date(date)) / (1000 * 60 * 60 * 24));
                      return (
                        <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: idx < majorDeadlines.length - 1 ? '1px solid #fecaca' : 'none', fontSize: '10px' }}>
                          <span style={{ color: '#0f172a', flex: 1, fontWeight: 500 }}>{task.description?.substring(0, 32)}{task.description?.length > 32 ? '…' : ''}</span>
                          <span style={{ fontWeight: 800, color: daysLeft <= 3 ? '#b91c1c' : daysLeft <= 7 ? '#ea580c' : '#047857', marginLeft: '8px', fontSize: '10px' }}>
                            {daysLeft === 0 ? 'TODAY' : daysLeft === 1 ? '1 day' : `${daysLeft} days`}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Daily Leadership Wisdom */}
                {wisdom && (
                  <div data-testid="leadership-wisdom-print" style={{ marginBottom: '16px', padding: '12px 14px', background: '#faf5ff', borderRadius: '6px', border: '1px solid #e9d5ff', borderLeft: '4px solid #7c3aed' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '6px' }}>
                      <div style={{ fontSize: '11px', fontWeight: 800, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Leadership Wisdom</div>
                      <div style={{ fontSize: '9px', color: '#7c3aed', fontWeight: 700 }}>Day {wisdom.day_number}/{wisdom.total_wisdoms}</div>
                    </div>
                    <div style={{ fontSize: '11px', fontWeight: 800, color: '#0f172a', marginBottom: '4px', lineHeight: 1.35 }}>{wisdom.tip}</div>
                    <div style={{ fontSize: '10px', color: '#334155', marginBottom: '6px', lineHeight: 1.45 }}>
                      <span style={{ fontWeight: 800, color: '#6b21a8' }}>Today&apos;s move: </span>{wisdom.action}
                    </div>
                    {wisdom.influence_line && (
                      <div style={{ fontSize: '10px', color: '#334155', fontStyle: 'italic', marginBottom: '6px', lineHeight: 1.45 }}>
                        <span style={{ fontWeight: 800, fontStyle: 'normal', color: '#6b21a8' }}>Influence: </span>{wisdom.influence_line}
                      </div>
                    )}
                    <div style={{ fontSize: '9px', color: '#7c3aed', textAlign: 'right', fontWeight: 700 }}>— {wisdom.book}, {wisdom.author}</div>
                  </div>
                )}

                {/* Day Notes — dedicated space for the printed day's notes,
                    decisions, and follow-ups. Sized for real hand-writing. */}
              </div>
            </div>

            {/* =================== DELEGATION + ROUNDING BAND =================== */}
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px', marginTop: '14px' }}>
              {/* Delegation Tracker */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0f172a', marginBottom: '4px', paddingBottom: '3px', borderBottom: '2px solid #3B82F6', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span>Delegated Today</span>
                  <span style={{ fontSize: '9px', color: '#3B82F6', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>Who · What · By when</span>
                </div>
                <div style={{ border: '1px solid #cbd5e1', borderRadius: '3px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr 70px', background: '#eff6ff', borderBottom: '1px solid #cbd5e1' }}>
                    {['WHO', 'WHAT', 'BY WHEN'].map((h, i) => (
                      <div key={h} style={{ fontSize: '9px', fontWeight: 800, color: '#1e40af', padding: '3px 6px', letterSpacing: '0.05em', borderLeft: i === 0 ? 'none' : '1px solid #cbd5e1' }}>{h}</div>
                    ))}
                  </div>
                  {[0, 1, 2, 3].map(idx => (
                    <div key={idx} style={{ display: 'grid', gridTemplateColumns: '90px 1fr 70px', borderTop: idx === 0 ? 'none' : '1px solid #e2e8f0', height: '20px' }}>
                      <div style={{ borderRight: '1px solid #e2e8f0' }} />
                      <div style={{ borderRight: '1px solid #e2e8f0' }} />
                      <div />
                    </div>
                  ))}
                </div>
              </div>
              {/* People Rounded On */}
              <div>
                <div style={{ fontSize: '12px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0f172a', marginBottom: '4px', paddingBottom: '3px', borderBottom: '2px solid #10B981' }}>
                  People I Rounded On
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '10px' }}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(n => (
                    <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '6px', height: '20px' }}>
                      <div style={{ width: '10px', height: '10px', border: '1.5px solid #10B981', borderRadius: '50%', flexShrink: 0 }} />
                      <div style={{ flex: 1, height: '14px', borderBottom: '1px solid #cbd5e1' }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* =================== DAY NOTES (Page 1, full-width) ===================
                Compact quick-scratch space — heavy writing happens on Page 2. */}
            <div style={{ marginTop: '12px' }}>
              <div style={{ fontSize: '11px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#0f172a', marginBottom: '5px', paddingBottom: '3px', borderBottom: '2px solid #7c3aed', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span>Quick Notes</span>
                <span style={{ fontSize: '9px', color: '#7c3aed', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>More space on Page 2</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '18px' }}>
                {Array.from({ length: 6 }).map((_, idx) => (
                  <div key={idx} style={{ height: '20px', borderBottom: '1px solid #cbd5e1' }} />
                ))}
              </div>
            </div>

            {/* =================== FOOTER (Page 1) =================== */}
            <div style={{ marginTop: '18px', paddingTop: '10px', borderTop: '2px solid #7c3aed', display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#475569', fontWeight: 600 }}>
              <span>Niko OS · Intelligent Command Center</span>
              <span>Page 1 of 2</span>
            </div>
          </div>
          
          {/* PAGE 2: Weekly Forecast + Follow-Up Log (Back Side) ---------- */}
          {/*
              Layout intent:
                • Top ⅔: 7-day forecast table (Sun→Sat around the planner date)
                  showing meetings + open task deadlines per day.
                • Bottom ⅓: Follow-Up Log — one row per weekday with 2-3 blank
                  lines to hand-write day events, decisions, and follow-ups.
              This replaces the previous monthly grid on Page 2.
           */}
          {(() => {
            // Build a 7-day window centered on the planner date (Sun-Sat)
            const baseDate = new Date(date + 'T12:00:00');
            const dayOfWeek = baseDate.getDay(); // 0=Sun
            const sunday = new Date(baseDate);
            sunday.setDate(baseDate.getDate() - dayOfWeek);
            const week = Array.from({ length: 7 }).map((_, i) => {
              const d = new Date(sunday);
              d.setDate(sunday.getDate() + i);
              return d;
            });
            const iso = (d) => d.toISOString().slice(0, 10);
            const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const rangeLabel = `${week[0].toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${week[6].toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

            // Meetings this week — pull from monthMeetings (already includes
            // recurring expansions) and fall back to `meetings` if empty.
            const weekMeetingsSource = (monthMeetings && monthMeetings.length > 0) ? monthMeetings : meetings;
            const meetingsByDay = {};
            week.forEach(d => { meetingsByDay[iso(d)] = []; });
            weekMeetingsSource.forEach(m => {
              if (m.date >= iso(week[0]) && m.date <= iso(week[6])) {
                meetingsByDay[m.date].push(m);
              }
            });
            Object.keys(meetingsByDay).forEach(k => {
              meetingsByDay[k].sort((a, b) => (a.start_time ?? 0) - (b.start_time ?? 0));
            });

            // Open task deadlines landing in the week
            const tasksByDay = {};
            week.forEach(d => { tasksByDay[iso(d)] = []; });
            tasks.forEach(t => {
              if (t.completed) return;
              const due = t.due_date || t.deadline;
              if (due && due >= iso(week[0]) && due <= iso(week[6])) {
                tasksByDay[due].push(t);
              }
            });

            const fmtTime = (t) => {
              if (typeof t !== 'number') return '';
              const h = Math.floor(t);
              const min = Math.round((t - h) * 60);
              const period = h >= 12 ? 'p' : 'a';
              const h12 = h % 12 === 0 ? 12 : h % 12;
              return min === 0 ? `${h12}${period}` : `${h12}:${String(min).padStart(2, '0')}${period}`;
            };

            const todayIso = new Date().toISOString().slice(0, 10);

            return (
              <div className="page-break pdf-page" style={{ pageBreakBefore: 'always', breakBefore: 'page', width: '768px', height: '985px', overflow: 'hidden', boxSizing: 'border-box', padding: '24px 40px' }}>
                {/* Header */}
                <div style={{ textAlign: 'center', paddingBottom: '10px', borderBottom: '3px solid #8B5CF6', marginBottom: '10px' }}>
                  <h1 style={{ fontSize: '22px', fontWeight: 800, color: '#000000', margin: 0, letterSpacing: '0.02em' }}>Day Journal</h1>
                  <div style={{ fontSize: '13px', color: '#333333', fontWeight: 600, marginTop: '3px' }}>{(() => {
                    const dObj = new Date(date + 'T12:00:00');
                    return dObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                  })()}</div>
                </div>

                {/* Rotating weekly prompt — rotates by ISO week of year */}
                {(() => {
                  const prompts = [
                    "This week I'm working on…",
                    "The person I want to invest in this week is…",
                    "The habit I'm building this week…",
                    "The one metric I'm watching this week…",
                    "Something I'll stop doing this week…",
                    "A conversation I've been avoiding that needs to happen…",
                    "This week's small experiment…",
                    "Where I want to be present this week…",
                  ];
                  // Rough ISO week seed by date
                  const startDate = new Date(iso(week[0]));
                  const dayNum = Math.floor((startDate - new Date(startDate.getFullYear(), 0, 0)) / 86400000);
                  const wk = Math.floor(dayNum / 7);
                  const prompt = prompts[wk % prompts.length];
                  return (
                    <div style={{ border: '1px solid #cbd5e1', borderLeft: '4px solid #7c3aed', borderRadius: '4px', padding: '6px 12px', marginBottom: '10px', background: '#faf5ff' }}>
                      <div style={{ fontSize: '9px', fontWeight: 800, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Weekly Prompt</div>
                      <div style={{ fontSize: '11px', fontStyle: 'italic', color: '#0f172a', marginTop: '1px' }}>{prompt}</div>
                      <div style={{ height: '16px', borderBottom: '1px solid #cbd5e1', marginTop: '4px' }} />
                    </div>
                  );
                })()}

                {/* Weekly Forecast removed by user request — freeing space for
                    more Day Events & Follow-Up writing lines below. */}

                {/* ── Day Events & Follow-Up — for the DAY OF printing only ── */}
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ fontSize: '14px', fontWeight: 800, textTransform: 'uppercase', color: '#0f172a', paddingBottom: '4px', borderBottom: '2px solid #8B5CF6', letterSpacing: '0.05em', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <span>Day Events &amp; Follow-Up</span>
                    <span style={{ fontSize: '10px', color: '#7c3aed', fontWeight: 600, textTransform: 'none', letterSpacing: 0 }}>
                      {(() => {
                        const dObj = new Date(date + 'T12:00:00');
                        return dObj.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
                      })()}
                    </span>
                  </div>
                  {/* Big two-column writing block for the printed day */}
                  <div style={{ border: '1.5px solid #0f172a', borderRadius: '4px', padding: '10px 12px', background: '#ffffff' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '20px' }}>
                      {Array.from({ length: 40 }).map((_, idx) => (
                        <div key={idx} style={{ height: '22px', borderBottom: '1px solid #cbd5e1' }} />
                      ))}
                    </div>
                  </div>
                </div>

                {/* ── Bright Spot + Regret ── */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '8px' }}>
                  <div style={{ border: '1px solid #10B981', borderLeft: '4px solid #10B981', borderRadius: '4px', padding: '6px 10px', background: '#f0fdf4' }}>
                    <div style={{ fontSize: '9px', fontWeight: 800, color: '#059669', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Bright Spot</div>
                    <div style={{ fontSize: '9px', color: '#059669', marginBottom: '3px' }}>Best moment today</div>
                    <div style={{ height: '16px', borderBottom: '1px solid #86efac' }} />
                    <div style={{ height: '16px', borderBottom: '1px solid #86efac', marginTop: '2px' }} />
                  </div>
                  <div style={{ border: '1px solid #F59E0B', borderLeft: '4px solid #F59E0B', borderRadius: '4px', padding: '6px 10px', background: '#fffbeb' }}>
                    <div style={{ fontSize: '9px', fontWeight: 800, color: '#b45309', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Redo</div>
                    <div style={{ fontSize: '9px', color: '#b45309', marginBottom: '3px' }}>One thing I&apos;d do over</div>
                    <div style={{ height: '16px', borderBottom: '1px solid #fcd34d' }} />
                    <div style={{ height: '16px', borderBottom: '1px solid #fcd34d', marginTop: '2px' }} />
                  </div>
                </div>

                {/* ── Shutdown Ritual ── end-of-day close-out */}
                <div style={{ border: '2px solid #0f172a', borderRadius: '4px', padding: '8px 12px', background: '#f1f5f9', marginBottom: '8px' }}>
                  <div style={{ fontSize: '10px', fontWeight: 800, color: '#0f172a', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '5px' }}>
                    Shutdown Ritual
                  </div>
                  {[
                    'One decision I made today',
                    'One person I appreciated',
                    'One thing to close by tomorrow',
                    "Tomorrow's One Bet",
                  ].map(label => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '3px' }}>
                      <div style={{ width: '10px', height: '10px', border: '1.5px solid #0f172a', borderRadius: '2px', flexShrink: 0 }} />
                      <span style={{ fontSize: '9px', fontWeight: 700, color: '#334155', width: '150px', flexShrink: 0 }}>{label}:</span>
                      <div style={{ flex: 1, height: '14px', borderBottom: '1px solid #94a3b8' }} />
                    </div>
                  ))}
                </div>

                {/* Facility Legend */}
                <div style={{ paddingTop: '6px', borderTop: '1px solid #cbd5e1' }}>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', fontSize: '10px', alignItems: 'center' }}>
                    <span style={{ fontWeight: 800, color: '#0f172a', letterSpacing: '0.03em' }}>LEGEND:</span>
                    {facilities.map(f => (
                      <div key={f.facility_id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: facilityColorMap[f.facility_id] || f.color || '#8B5CF6' }} />
                        <span style={{ color: '#334155' }}>{f.short_name || f.name}</span>
                      </div>
                    ))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: PERSONAL_COLOR }} />
                      <span style={{ color: '#334155' }}>Personal</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ color: '#dc2626', fontWeight: 800 }}>◆</span>
                      <span style={{ color: '#334155' }}>Task deadline</span>
                    </div>
                  </div>
                </div>

                {/* Footer Page 2 */}
                <div style={{ marginTop: '8px', paddingTop: '6px', borderTop: '2px solid #7c3aed', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '9px', color: '#475569', fontWeight: 600 }}>
                  <span>Niko OS · Intelligent Command Center</span>
                  <span style={{ fontWeight: 700, color: '#7c3aed' }}>Log notes → nikosafedirector.com</span>
                  <span>Page 2 of 2</span>
                </div>
              </div>
            );
          })()}
          {/* END PAGE 2 --------------------------------------------------- */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
